import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { EntityDetailsHeader } from '@client/components/_ui/data-display/EntityDetailsHeader';
import {
    ENTITY_HEADER_FLAGS_COLUMN_CLASS,
    EntityHeaderFlags,
} from '@client/components/_ui/data-display/EntityHeaderFlags';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ClaimFlagTag } from '@client/components/claims/ClaimFlagTag';
import { ClaimHeaderClientUpdate } from '@client/components/claims/ClaimHeaderClientUpdate';
import { ClaimStatesBar } from '@client/components/claims/ClaimStatesBar';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { ClaimClosedOutcomeCallout } from '@client/components/claims/closure/ClaimClosedOutcome';
import {
    ClaimCompletionStatus,
    hasClaimCompletionToReport,
    useClaimCompletionAvailability,
} from '@client/components/claims/completion/ClaimCompletionStatus';
import { ChangeProductButton } from '@client/components/claims/product-binding/ChangeClaimProduct';
import {
    ChooseProductButton,
    ChooseProductHost,
    ClaimAwaitingProductBanner,
    useChooseProductAccess,
} from '@client/components/claims/product-binding/ClaimAwaitingProduct';
import {
    FastTrackChip,
    FraudWatchChip,
    ProceedChip,
    ReadinessChip,
    UrgencyChip,
} from '@client/components/claims/unified-assessment/chips';
import { ContactPopover } from '@client/components/contacts/ContactPopover';
import { useQueryErrorEffect } from '@client/hooks/api/useQueryErrorEffect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useClaimDeferredBindingEnabled } from '@client/hooks/business/claim/useClaimDeferredBinding';
import { useClaimStatesCapability } from '@client/hooks/business/claim/useClaimStatesCapability';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { ICONS } from '@client/styles/theme';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import type { ShareableRouteContext } from '@client/types/router';
import { formatShortVersion } from '@client/utils/formatters/version-label';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { DISPLAY_COLOR_CONFIGURATION } from '@client/utils/ui/display-configuration';
import { isVehicleProductCategory } from '@shared/constants';
import { hasClaimUpdateAccess } from '@shared/permissions/claim';
import type { ClaimFlagType } from '@shared/prisma/enums';
import { isClaimAwaitingProduct } from '@shared/utils/business/claim-product-model';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { Tabs as AntTabs, Badge, Skeleton } from 'antd';
import { Decimal } from 'decimal.js';
import { Car } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { match } from 'ts-pattern';
import { runDetailRoutePrefetchWhenAuthorised } from '../../detail-route-prefetch';
import { Actions } from './claim-details-actions';
import { isClaimDetailPrefetchAuthorised, prefetchClaimRecord, useClaimRecordQuery } from './claim-record-query';
import { getProductTypeDataTabLabel } from './product-type-data-tab';
import { claimFormStore, useInitClaimForm } from './store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id')({
    staticData: {
        breadcrumb: {
            title: 'Claim details',
        },
        ui: {
            widthVariant: 'default',
        },
        entity: 'claim',
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    beforeLoad: ({ context, params, preload, abortController }): ShareableRouteContext => {
        // Kick off the LCP-critical `getClaim` while the route's chunks are still
        // being parsed and the auth gate is still rendering — otherwise it cannot
        // start until ClaimDetails mounts. Same pattern as the incident and
        // complaint detail routes. The gate mirrors the `/console/$orgId/claims`
        // layout (roles and the claim module).
        // On a cold deep link the session may still be restoring, so the prefetch
        // is deferred until the auth store becomes authorised. Hover-intent
        // preloads never defer; leaving the match aborts the deferral.
        runDetailRoutePrefetchWhenAuthorised(
            () => isClaimDetailPrefetchAuthorised(params.orgId),
            () => prefetchClaimRecord(context.trpcUtils, params.id, { preload }),
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimDetails />
        </PageController>
    );
}

const zeroDecimal = new Decimal(0);

function HeaderIncurredCost({
    isLoading,
    reserve: reserveProp,
    recovery: recoveryProp,
    excess: excessProp,
}: {
    isLoading: boolean;
    reserve?: Decimal;
    recovery?: Decimal;
    excess?: Decimal;
}) {
    const { formatCurrency } = useFormatter();

    const [type, setType] = useState<'net' | 'total'>('total');

    const reserve = reserveProp || zeroDecimal;
    const recovery = recoveryProp || zeroDecimal;
    const excess = excessProp || zeroDecimal;

    return (
        <div className="flex flex-col gap-0.5 rounded-lg border border-separator bg-card px-3 py-2">
            <span className="text-muted-foreground text-xs">Costs incurred</span>
            <div className="flex items-center gap-2">
                <span className="font-medium text-lg">
                    {isLoading ? (
                        <Skeleton.Node
                            active
                            classNames={{
                                content: 'w-20 h-7 mb-0.5',
                            }}
                        />
                    ) : (
                        formatCurrency(type === 'net' ? Decimal.max(0, reserve.minus(recovery).minus(excess)) : reserve)
                    )}
                </span>
                <SegmentedControl
                    value={type}
                    onChange={setType}
                    size="small"
                    options={[
                        {
                            label: 'Total',
                            value: 'total',
                            tooltip: 'Show total cost incurred for the claim. This is the reserve amount.',
                        },
                        {
                            label: 'Net',
                            value: 'net',
                            tooltip:
                                'Show net cost incurred for the claim. This is the total cost incurred minus the recovery and excess amounts.',
                        },
                    ]}
                />
            </div>
        </div>
    );
}

function Header() {
    const claim = claimFormStore.use.formData();
    const enableHeaderTriageChips = useFeatureFlag('enable-header-triage-chips');
    const enableAiTriage = useFeatureFlag('enable-ai-triage', false);
    const getTriageQuery = trpc.claim.aiTriage.getTriage.useQuery(
        claim.id && enableHeaderTriageChips && enableAiTriage ? { claimId: claim.id } : skipToken,
    );
    const getFinancialSummaryQuery = trpc.claim.getFinancialSummary.useQuery({ claimId: claim.id });
    const { formatDate } = useFormatter();

    const triageAssessment = getTriageQuery.data?.assessment;

    const policyHolderContact = claim.Contacts?.find(({ type }) => type === 'POLICY_HOLDER');
    const insurerContact = claim.Contacts?.find(({ type }) => type === 'INSURER');
    const brokerContact = claim.Contacts?.find(({ type }) => type === 'BROKER');

    const primaryClaimFlagRow = claim.ClaimFlags.find((item) => item.type === 'PRIMARY');
    const primaryClaimFlag = primaryClaimFlagRow
        ? {
              name: primaryClaimFlagRow.ClaimFlag.name,
              displayColor: primaryClaimFlagRow.ClaimFlag.displayColor,
              isSystemDefined: primaryClaimFlagRow.ClaimFlag.isSystemDefined,
          }
        : undefined;
    const secondaryClaimFlags =
        claim.ClaimFlags.filter((item) => item.type === 'SECONDARY').map((item) => ({
            name: item.ClaimFlag.name,
            type: item.type,
            displayColor: item.ClaimFlag.displayColor,
            isSystemDefined: item.ClaimFlag.isSystemDefined,
        })) || [];
    const labelClaimFlags =
        claim.ClaimFlags.filter((item) => item.type === 'LABEL').map((item) => ({
            name: item.ClaimFlag.name,
            type: item.type as Extract<ClaimFlagType, 'LABEL'>,
            isSystemDefined: item.ClaimFlag.isSystemDefined,
        })) || [];
    const flagConfiguration = primaryClaimFlag ? DISPLAY_COLOR_CONFIGURATION[primaryClaimFlag.displayColor] : null;
    // A claim closed without its product (CUR-2167) no longer waits for one.
    const awaitingProduct = isClaimAwaitingProduct(claim) && claim.Status.type !== 'CLOSED';
    const closedWithoutProduct = isClaimAwaitingProduct(claim) && claim.Status.type === 'CLOSED';
    const chooseProductAccess = useChooseProductAccess(claim);
    const deferredBindingEnabled = useClaimDeferredBindingEnabled();
    const completion = useClaimCompletionAvailability(claim);

    if (getTriageQuery.error ?? getFinancialSummaryQuery.error) {
        return (
            <InlineQueryError
                error={getTriageQuery.error ?? getFinancialSummaryQuery.error}
                onRetry={() => retryFailedQueries([getTriageQuery, getFinancialSummaryQuery])}
                retrying={isRetryingQueries([getTriageQuery, getFinancialSummaryQuery])}
            />
        );
    }

    return (
        <EntityDetailsHeader
            // The primary flag colours the card's border and its thicker left edge, never its surface:
            // tinting the whole card put every pill and tag on a background they were not measured on
            // (outlined tags lost their edge on the pale field).
            classNames={flagConfiguration ? { card: flagConfiguration.filled.borderColorClass } : undefined}
            accentClassName={flagConfiguration?.filled.borderColorClass}
            title={claim.generatedId || String(claim.id) || '-'}
            titleAddon={
                <>
                    <ClaimStatusTag variant="outlined" data={claim.Status} />
                    {awaitingProduct && <StatusBadge module="claimProductBinding" status="PENDING" />}
                    {claim.Status.type === 'CLOSED' && claim.closureOutcome && (
                        <StatusBadge module="claimClosureOutcome" status={claim.closureOutcome} />
                    )}
                    {claim.createdAt && (
                        <span className="text-muted-foreground text-xs">Lodged {formatDate(claim.createdAt)}</span>
                    )}
                    {claim.claimOccurredAt && (
                        <span className="text-muted-foreground text-xs">Loss {formatDate(claim.claimOccurredAt)}</span>
                    )}
                </>
            }
            mainInfo={[
                // A claim recorded before its product is known offers the choice where the product
                // will show (CUR-2163); someone who cannot choose it reads its state instead.
                awaitingProduct
                    ? {
                          title: 'Product',
                          children:
                              chooseProductAccess.canChoose || chooseProductAccess.reason ? (
                                  <ChooseProductButton claim={claim} variant="inline" />
                              ) : (
                                  'Awaiting product'
                              ),
                      }
                    : closedWithoutProduct
                      ? { title: 'Product', children: 'No product' }
                      : // A claim on a product names it, with the way to move it to another (CUR-2193);
                        // a v1 claim keeps its product type.
                        deferredBindingEnabled && claim.ProductVersion
                        ? {
                              title: 'Product',
                              children: (
                                  <span className="flex flex-wrap items-baseline gap-x-2">
                                      <span>{claim.ProductVersion.Product.name}</span>
                                      <span className="font-normal text-muted-foreground">
                                          {formatShortVersion(claim.ProductVersion.version)}
                                      </span>
                                      <ChangeProductButton claim={claim} />
                                  </span>
                              ),
                          }
                        : {
                              title: 'Product type',
                              children: claim.ProductType?.name || '-',
                          },
                // Where the request for the client to complete the claim stands (CUR-2195), once one was
                // sent; asking is in the toolbar until then.
                ...(completion && hasClaimCompletionToReport(completion)
                    ? [
                          {
                              title: 'Client',
                              children: <ClaimCompletionStatus claimId={claim.id} availability={completion} />,
                          },
                      ]
                    : []),
                {
                    title: 'Policy holder',
                    children: claim.phCompany || policyHolderContact?.fullName || '-',
                    ...(policyHolderContact?.id && {
                        className: 'cursor-pointer transition hover:opacity-70',
                        renderWrapper({ children }) {
                            return <ContactPopover id={policyHolderContact.id}>{children}</ContactPopover>;
                        },
                    }),
                },
                {
                    title: 'Insurer',
                    children: claim.Insurer?.name || '-',
                    ...(insurerContact?.id && {
                        className: 'cursor-pointer transition hover:opacity-70',
                        renderWrapper({ children }) {
                            return <ContactPopover id={insurerContact.id}>{children}</ContactPopover>;
                        },
                    }),
                },
                {
                    title: 'Broker',
                    children: claim.brokerCompany || '-',
                    ...(brokerContact?.id && {
                        className: 'cursor-pointer transition hover:opacity-70',
                        renderWrapper({ children }) {
                            return <ContactPopover id={brokerContact.id}>{children}</ContactPopover>;
                        },
                    }),
                },
                ...(claim.ClaimFlags.length
                    ? [
                          {
                              title: 'Flags',
                              className: ENTITY_HEADER_FLAGS_COLUMN_CLASS,
                              children: (
                                  <EntityHeaderFlags
                                      primary={
                                          primaryClaimFlag && (
                                              <ClaimFlagTag data={{ ...primaryClaimFlag, type: 'PRIMARY' }} />
                                          )
                                      }
                                      secondary={secondaryClaimFlags.map((item) => (
                                          <ClaimFlagTag key={item.name} data={item} />
                                      ))}
                                      labels={labelClaimFlags.map((item) => (
                                          <ClaimFlagTag key={item.name} data={item} />
                                      ))}
                                  />
                              ),
                          },
                      ]
                    : []),
            ]}
            rightAddon={
                <>
                    <HeaderIncurredCost
                        isLoading={getFinancialSummaryQuery.isLoading}
                        reserve={getFinancialSummaryQuery.data?.totalReserveAmount}
                        recovery={getFinancialSummaryQuery.data?.recoveryAmount}
                        excess={getFinancialSummaryQuery.data?.excessAmount}
                    />
                    <ClaimHeaderClientUpdate claim={claim} />
                </>
            }
            bottomAddon={
                enableHeaderTriageChips &&
                triageAssessment && (
                    <div className="flex flex-wrap items-center gap-1">
                        <UrgencyChip
                            level={triageAssessment.urgency.level}
                            reasoning={triageAssessment.urgency.reasoning}
                        />
                        <ReadinessChip
                            status={triageAssessment.triageReadiness.status}
                            reasoning={triageAssessment.triageReadiness.reasoning}
                            missingFields={triageAssessment.triageReadiness.missingFields}
                        />
                        <ProceedChip
                            status={triageAssessment.proceedRecommendation.status}
                            reasoning={triageAssessment.proceedRecommendation.reasoning}
                        />
                        <FastTrackChip
                            suitable={triageAssessment.fastTrackSuitability.suitable}
                            reasoning={triageAssessment.fastTrackSuitability.reasoning}
                            blockers={triageAssessment.fastTrackSuitability.blockers}
                        />
                        <FraudWatchChip
                            level={triageAssessment.fraudWatch.level}
                            reasoning={triageAssessment.fraudWatch.reasoning}
                            signals={triageAssessment.fraudWatch.signals}
                        />
                    </div>
                )
            }
        />
    );
}

function ClaimDetails() {
    const orgId = useOrgId();
    const currentOrg = useOrg();
    const enableUnifiedAiAssessment = useFeatureFlag('enable-ai-unified-assessment');
    const { isEnabled: enableClaimStates } = useClaimStatesCapability();
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const claimQuery = useClaimRecordQuery(id);
    useQueryErrorEffect(claimQuery.error, (error) => {
        message.destroy();

        match(error.data?.errorId)
            .with('recordNotFound', () => {
                void errorMessage
                    .showAsync(
                        "The claim you are trying to access either doesn't exist or you don't have permissions to access it.",
                    )
                    .then(() => {
                        navigate({
                            to: '/console/$orgId/claims',
                            params: { orgId },
                        });
                    });
            })
            .otherwise(() => {
                errorMessage.show(error);
            });
    });
    const user = useUser();
    const { mutateAsync } = trpc.claim.updateClaim.useMutation();

    const [input] = useState<RouterInputs['notification']['getUnreadNotificationCount']>({
        where: {
            isActive: true,
            CommLog: {
                claimId: id,
            },
        },
    });

    const { data: unreadNotificationCount } = trpc.notification.getUnreadNotificationCount.useQuery(input, {
        refetchInterval: 1000 * 60,
    });

    const { formData: claim } = useInitClaimForm({
        data: claimQuery.data,
        isLoading: claimQuery.isLoading,

        async onReload() {
            await claimQuery.refetch();
        },

        async onSave(_, changedValues) {
            try {
                await mutateAsync({
                    id,
                    set: changedValues,
                });

                await trpcUtils.claim.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    useCustomDocumentTitle(`Claim - ${claim?.generatedId || id} - Curium`);

    const { pathname } = useLocation();

    // LCP guardrail (CUR-1036, task 0.1): the page's content is ready once the current
    // claim record has resolved — the static chrome below painting earlier must not
    // move this.
    useContentReadyMark(Boolean(claim && id === claim.id));

    const productTypeDataTabLabel = claim
        ? getProductTypeDataTabLabel(claim, { isActive: pathname.endsWith('/product-type-data') })
        : null;

    return claim && id === claim.id ? (
        <>
            <Header />
            {enableClaimStates && (
                <ClaimStatesBar
                    claimId={claim.id}
                    statesSnapshot={claim.statesSnapshot}
                    currentStatePosition={claim.currentStatePosition}
                    statesCompletedAt={claim.statesCompletedAt}
                    isClaimClosed={claim.Status.type === 'CLOSED'}
                    canAdvance={hasClaimUpdateAccess(user, claim)}
                />
            )}
            <Actions />
            <ClaimClosedOutcomeCallout claim={claim} />
            <ClaimAwaitingProductBanner claim={claim} />
            <ChooseProductHost claimId={claim.id} currentVersionId={claim.productVersionId} />
            {/* Up to 13 tabs: the ones that do not fit go into a "More" menu beside the tablist, not
                Ant's "…" trigger inside it (CUR-1360). */}
            <Tabs
                overflowMenu
                items={[
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.manage size={16} />
                                <span>Manage claim</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/manage' as const,
                    },
                    ...(enableUnifiedAiAssessment
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.ai size={16} />
                                          <span>AI assessment</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/ai-assessment' as const,
                              },
                          ]
                        : []),
                    // One tab for the whole vehicle workstream — the AI damage scan (Ravin AI)
                    // and the repair assessment (Clear21) are sections within it. Shown only for
                    // vehicle product types; a product type merely *named* "Motor" does not
                    // qualify until its category is set in Configuration → Product types.
                    ...(isVehicleProductCategory(claim.ProductType?.category)
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <Car size={16} />
                                          <span>Vehicle</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/vehicle' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.policyInfo size={16} />
                                <span>Policy info</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/policy-info' as const,
                    },
                    // Products v2: the version's product fields (lodged answers plus handler
                    // fields). Its own tab by decision — Manage claim is already busy — and
                    // only for claims on a product version; the tab shows its own empty state.
                    ...(claim.productVersionId
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.productType size={16} />
                                          <span>Product fields</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/product-fields' as const,
                              },
                          ]
                        : []),
                    ...(productTypeDataTabLabel
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.claim size={16} />
                                          <span>{productTypeDataTabLabel}</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/product-type-data' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="relative flex items-center gap-2">
                                <ICONS.communication size={16} />
                                <span>Communication</span>
                                {(unreadNotificationCount?.count || 0) > 0 && (
                                    <Badge
                                        count={unreadNotificationCount?.count || 0}
                                        size="small"
                                        className="absolute -top-2 -right-4"
                                    />
                                )}
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/comms' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.activity size={16} />
                                <span>Activity</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/activity' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.contacts size={16} />
                                <span>Contacts</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/contacts' as const,
                    },
                    ...(currentOrg.enabledModules.includes('billing')
                        ? [
                              {
                                  label: (
                                      <span className="flex items-center gap-2">
                                          <ICONS.expenses size={16} />
                                          <span>Expenses and billing</span>
                                      </span>
                                  ),
                                  to: '/console/$orgId/claims/details/$id/expenses' as const,
                              },
                          ]
                        : []),
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.financials size={16} />
                                <span>Financials</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/financials' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.library size={16} />
                                <span>Library</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/library' as const,
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.history size={16} />
                                <span>History</span>
                            </span>
                        ),
                        to: '/console/$orgId/claims/details/$id/history' as const,
                    },
                ]}
            />
        </>
    ) : (
        // Static chrome before data (CUR-1036, task 2.5). The real `Tabs` wrapper above
        // mounts the child route via `<Outlet />`, and the manage tab reads the claim
        // form store the record populates — so pre-data this renders a visual-only strip
        // (plain antd Tabs, inert clicks) of the record-INDEPENDENT tabs in their final
        // position, a dimension-reserving header skeleton, and an empty spacer where the
        // action row lands. Record-dependent tabs (Vehicle, product-type data) and the
        // states bar appear once the record resolves. Keep these labels in sync with the
        // real items above.
        <>
            <Skeleton active title paragraph={{ rows: 1 }} className="mb-4" />
            <div className="mb-4 flex h-8 items-center gap-2" />
            <div role="region" aria-busy="true" aria-label="Loading claim details">
                <AntTabs
                    activeKey={
                        STATIC_CLAIM_TAB_KEYS.includes(pathname.split('/')[6] ?? '') ? pathname.split('/')[6] : 'manage'
                    }
                    items={[
                        staticClaimTab('manage', <ICONS.manage size={16} />, 'Manage claim'),
                        ...(enableUnifiedAiAssessment
                            ? [staticClaimTab('ai-assessment', <ICONS.ai size={16} />, 'AI assessment')]
                            : []),
                        staticClaimTab('policy-info', <ICONS.policyInfo size={16} />, 'Policy info'),
                        staticClaimTab('comms', <ICONS.communication size={16} />, 'Communication'),
                        staticClaimTab('activity', <ICONS.activity size={16} />, 'Activity'),
                        staticClaimTab('contacts', <ICONS.contacts size={16} />, 'Contacts'),
                        ...(currentOrg.enabledModules.includes('billing')
                            ? [staticClaimTab('expenses', <ICONS.expenses size={16} />, 'Expenses and billing')]
                            : []),
                        staticClaimTab('financials', <ICONS.financials size={16} />, 'Financials'),
                        staticClaimTab('library', <ICONS.library size={16} />, 'Library'),
                        staticClaimTab('history', <ICONS.history size={16} />, 'History'),
                    ]}
                />
            </div>
        </>
    );
}

/** Path segments of the record-independent claim tabs (pre-data strip highlight only). */
const STATIC_CLAIM_TAB_KEYS = [
    'manage',
    'ai-assessment',
    'policy-info',
    'comms',
    'activity',
    'contacts',
    'expenses',
    'financials',
    'library',
    'history',
];

function staticClaimTab(key: string, icon: ReactNode, text: string) {
    return {
        key,
        disabled: true,
        label: (
            <span className="flex items-center gap-2">
                {icon}
                <span>{text}</span>
            </span>
        ),
        children: <Skeleton active className="mt-4" />,
    };
}
